import { useState } from 'react'
import type { Color } from '@nhanho/shared'
import { colors as api, explainError } from '../../lib/api'
import { useList } from '../../lib/useList'
import { useToast } from '../../lib/toast'
import { PageHeader } from '../../components/ui/PageHeader'
import { Button } from '../../components/ui/Button'
import { Input } from '../../components/ui/Input'
import { Field } from '../../components/ui/Field'
import { Modal, ConfirmDialog } from '../../components/ui/Modal'
import { EmptyState } from '../../components/ui/EmptyState'
import { Spinner } from '../../components/ui/Spinner'

const EMPTY = { name: '', hex: '#8FC4E5' }

export default function ColorList() {
  const { data, loading, refresh } = useList<Color>((p) => api.list({ ...p, limit: 200, sort: 'order,name' }))
  const toast = useToast()
  const [editing, setEditing] = useState<Color | 'new' | null>(null)
  const [form, setForm] = useState(EMPTY)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [busy, setBusy] = useState(false)
  const [del, setDel] = useState<Color | null>(null)

  const open = (c: Color | 'new') => {
    setEditing(c)
    setForm(c === 'new' ? EMPTY : { name: c.name, hex: c.hex })
    setErrors({})
  }

  const save = async () => {
    setBusy(true); setErrors({})
    try {
      if (editing === 'new') await api.create(form)
      else if (editing) await api.update(editing.id, form)
      toast.success('Đã lưu màu')
      setEditing(null); refresh()
    } catch (e) {
      const { message, fieldErrors } = explainError(e)
      setErrors(fieldErrors)
      toast.error(message)
    } finally { setBusy(false) }
  }

  const remove = async () => {
    if (!del) return
    setBusy(true)
    try { await api.remove(del.id); toast.success('Đã xoá'); setDel(null); refresh() }
    catch (e) { toast.error(explainError(e).message) }
    finally { setBusy(false) }
  }

  return (
    <>
      <PageHeader title="Màu sắc" subtitle="Bảng màu dùng chung — sản phẩm và biến thể tham chiếu vào đây" actions={<Button onClick={() => open('new')}>+ Thêm màu</Button>} />
      {loading && !data ? <Spinner /> : !data?.docs.length ? (
        <EmptyState title="Chưa có màu nào" hint="Thêm màu để gán cho sản phẩm và biến thể." action={<Button onClick={() => open('new')}>+ Thêm màu</Button>} />
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {data.docs.map((c) => (
            <div key={c.id} className="flex items-center gap-3 rounded-card border border-border bg-white p-3 shadow-card">
              <span className="size-10 shrink-0 rounded-full border border-border" style={{ backgroundColor: c.hex }} aria-hidden />
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-medium text-ink">{c.name}</div>
                <div className="font-mono text-xs text-ink-muted">{c.hex}</div>
              </div>
              <Button variant="ghost" size="sm" onClick={() => open(c)}>Sửa</Button>
              <Button variant="ghost" size="sm" className="text-danger" onClick={() => setDel(c)}>Xoá</Button>
            </div>
          ))}
        </div>
      )}

      <Modal open={editing !== null} onClose={() => setEditing(null)} title={editing === 'new' ? 'Thêm màu' : 'Sửa màu'}
        footer={<><Button variant="secondary" onClick={() => setEditing(null)}>Huỷ</Button><Button onClick={save} loading={busy}>Lưu</Button></>}>
        <div className="space-y-4">
          <Field label="Tên màu" required error={errors.name}><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Hồng pastel" autoFocus /></Field>
          <Field label="Mã màu" required error={errors.hex} hint="Dạng #RRGGBB">
            <div className="flex items-center gap-3">
              <input type="color" value={/^#[0-9a-fA-F]{6}$/.test(form.hex) ? form.hex : '#000000'} onChange={(e) => setForm({ ...form, hex: e.target.value.toUpperCase() })} className="size-10 cursor-pointer rounded-input border border-border-strong" aria-label="Chọn màu" />
              <Input value={form.hex} onChange={(e) => setForm({ ...form, hex: e.target.value })} className="font-mono uppercase" maxLength={7} />
            </div>
          </Field>
        </div>
      </Modal>
      <ConfirmDialog open={!!del} onCancel={() => setDel(null)} onConfirm={remove} loading={busy} title="Xoá màu"
        message={<>Xoá màu <strong>{del?.name}</strong>? Nếu còn sản phẩm đang dùng, hệ thống sẽ từ chối.</>} />
    </>
  )
}
